import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Send, CheckCircle2, XCircle, BookCheck, RotateCcw, Ban, Plus, FileText, History, Paperclip, ShieldCheck, Search, X, Coins, ExternalLink, ChevronRight, Banknote, AlertTriangle,
} from 'lucide-react';

import { MasterReportPreview }  from '@/components/master/report/MasterReportPreview';
import { ApPaymentVoucher }      from '@/components/ap/ApPaymentVoucher';
import { exportToExcel }         from '@/utils/reportExport';
import { apPaymentApi }  from '@/api/apPayment.api';
import { supplierApi }   from '@/api/supplier.api';
import { accountApi }    from '@/api/account.api';
import { taxApi }        from '@/api/tax.api';
import { withheldOnNet } from '@/utils/withholding';
import type { WithholdingTaxRule } from '@/api/tax.api';
import { FundingSourceSelect } from '@/components/finance/FundingSourceSelect';
import { currencyApi }   from '@/api/currency.api';
import { exchangeRateApi } from '@/api/exchangeRate.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { AppRoutes }     from '@/constants/routes';
import { Spinner }       from '@/components/ui/Spinner';
import { Button }        from '@/components/ui/Button';
import { IconButton }    from '@/components/ui/IconButton';
import { Input }         from '@/components/ui/Input';
import { Select }        from '@/components/ui/Select';
import { Field }         from '@/components/ui/Field';
import { Tabs }          from '@/components/ui/Tabs';
import { ReasonDialog }  from '@/components/ui/ReasonDialog';
import { PageHeader }    from '@/components/layout/PageHeader';
import { MasterToolbar } from '@/components/master/MasterToolbar';
import { MasterFlash }   from '@/components/master/MasterFlash';
import { LinesGrid, type LineColumn } from '@/components/grids/LinesGrid';
import { MasterSearchModal } from '@/components/master/MasterSearchModal';
import { ERPContextBar }    from '@/components/erp/ERPContextBar';
import { WorkflowStatusPanel } from '@/components/erp/WorkflowStatusPanel';
import { AttachmentPanel }   from '@/components/erp/AttachmentPanel';
import { AuditViewer }       from '@/components/erp/AuditViewer';
import { setErpContext, clearErpContext } from '@/state/erpContextSlice';
import { cn }            from '@/utils/cn';

import type { RootState }          from '@/state/store';
import { deriveVoucherPaymentMethod } from '@/utils/voucherPaymentMethod';
import { costCentresForLocation } from '@/utils/costCentreScope';
import type { SearchColumn }       from '@/components/master/MasterSearchModal';
import type { SupplierListDto }    from '@/types/supplier.types';
import type {
  ApPaymentDto,
  CreateApPaymentRequest,
  UpdateApPaymentRequest,
  ApPaymentLineRequest,
  ApPaymentAllocationRequest,
} from '@/types/apPayment.types';
import { VoucherAllocationPicker } from '@/components/fin/VoucherAllocationPicker';
import { ChequeNumberField } from '@/components/banking/ChequeNumberField';
import { ChequeCrossingField } from '@/components/banking/ChequeCrossingField';
import { ChequeBookGuidance } from '@/components/banking/ChequeBookGuidance';
import { clearedByMethodChange } from '@/utils/paymentLineFields';
import type {
  VoucherAllocationPick, VoucherAllocationSummary, SavedVoucherAllocation,
} from '@/components/fin/VoucherAllocationPicker';

// ── Payment method definitions ────────────────────────────────────────────────

type PaymentMethodType =
  | 'Cash'
  | 'Cheque'
  | 'BankTransfer'
  | 'CreditCard'
  | 'DebitCard'
  | 'OnlineBanking'
  | 'PettyCash'
  | 'Mixed'
  | 'Other';

const PAYMENT_METHODS: PaymentMethodType[] = [
  'Cash', 'Cheque', 'BankTransfer', 'CreditCard', 'DebitCard', 'OnlineBanking', 'PettyCash', 'Other',
];   // 'Mixed' is deliberately absent: the server derives it from the lines, nobody picks it.

const PAYMENT_METHOD_LABELS: Record<PaymentMethodType, string> = {
  Cash:          'Cash',
  Cheque:        'Cheque',
  BankTransfer:  'Bank Transfer',
  CreditCard:    'Credit Card',
  DebitCard:     'Debit Card',
  OnlineBanking: 'Online Banking',
  // Its own method, not a flavour of Cash: the money leaves a named box whose balance moves with
  // it, and "Pay from" then offers floats rather than accounts (GAP-012).
  PettyCash:     'Petty Cash',
  // Only ever arrives from the server, on a voucher whose lines use more than one method.
  Mixed:         'Mixed',
  Other:         'Other',
};

// ── Search columns ────────────────────────────────────────────────────────────

const SUPPLIER_COLUMNS: SearchColumn<SupplierListDto>[] = [
  { key: 'code', label: 'Code', className: 'w-28 font-mono text-orange-700 dark:text-orange-400' },
  { key: 'name', label: 'Supplier Name' },
  { key: 'currencyCode', label: 'CCY', className: 'w-16 font-mono text-slate-500 dark:text-gray-400' },
];

// ── Line row ──────────────────────────────────────────────────────────────────

interface PaymentLineRow {
  key:           string;
  lineNumber:    number;
  paymentType:   PaymentMethodType;
  bankAccountId: string;
  /** The received cheque this line hands over, when it endorses one (ML-12). */
  endorsedChequeId: string;
  /** Cash GL account this line draws on. Empty for bank methods, which resolve via the bank. */
  fundingGlAccountId: string;
  /** The petty cash float the money leaves. Set only on a Petty Cash line (GAP-012). */
  pettyCashFloatId: string;
  /** The cheque book the number was taken from, in automatic numbering mode (GAP-001). */
  chequeBookId: string;
  chequeNumber:  string;
  /** How this cheque is crossed, when the voucher departs from configuration (ML-09). Empty
   *  says nothing and leaves it to the payable account, then the bank account. */
  crossingType:  string;
  chequeDate:    string;
  cardReference: string;
  amount:        number;
  currencyCode:  string;
  exchangeRate:  number;
  remarks:       string;
}

const mkLine = (n: number, ccy: string, xr: number): PaymentLineRow => ({
  key:           `new-${Date.now()}-${n}-${Math.random()}`,
  lineNumber:    n,
  paymentType:   'Cash',
  bankAccountId: '',
  fundingGlAccountId: '',
  pettyCashFloatId: '',
  chequeBookId:  '',
  chequeNumber:  '',
  crossingType:  '',
  endorsedChequeId: '',
  chequeDate:    '',
  cardReference: '',
  amount:        0,
  currencyCode:  ccy,
  exchangeRate:  xr,
  remarks:       '',
});

// ── Tabs ──────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'lines',       label: 'Payment Lines', icon: FileText    },
  { key: 'settles',     label: 'Bills Settled', icon: Coins       },
  { key: 'workflow',    label: 'Workflow',       icon: History     },
  { key: 'attachments', label: 'Attachments',    icon: Paperclip   },
  { key: 'audit',       label: 'Audit Log',      icon: ShieldCheck },
] as const;
type Tab = (typeof TABS)[number]['key'];

// ── Workflow status colours ───────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  Draft:           'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400',
  Submitted:       'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  PendingApproval: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  Approved:        'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  Rejected:        'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  Posted:          'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
  Reversed:        'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
};

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Main Component ────────────────────────────────────────────────────────────

export const ApPaymentFormPage = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew    = !id;

  const { hasPermission } = usePermission();
  const canEdit    = hasPermission(Permissions.AP_PAYMENT_EDIT);
  const canRaiseOnAccount = hasPermission(Permissions.AP_PAYMENT_ON_ACCOUNT);
  const canOverridePayee = hasPermission(Permissions.AP_PAYMENT_OVERRIDE_PAYEE);
  const canOverrideCrossing = hasPermission(Permissions.BNK_CHEQUE_CROSSING);
  const canSubmit  = hasPermission(Permissions.AP_PAYMENT_SUBMIT);
  const canApprove = hasPermission(Permissions.AP_PAYMENT_APPROVE);
  const canReject  = hasPermission(Permissions.AP_PAYMENT_REJECT);
  const canPost    = hasPermission(Permissions.AP_PAYMENT_POST);
  const canReverse = hasPermission(Permissions.AP_PAYMENT_REVERSE);
  const canCancel  = hasPermission(Permissions.AP_PAYMENT_CANCEL);
  const canDelete  = hasPermission(Permissions.AP_PAYMENT_DELETE);
  const canAddDoc  = hasPermission(Permissions.DOC_ATTACHMENT_ADD);
  const canDelDoc  = hasPermission(Permissions.DOC_ATTACHMENT_DELETE);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const session    = useSelector((s: RootState) => s.session.session);
  const accessibleLocations   = useSelector((s: RootState) => s.auth.accessibleLocations);
  const accessibleCostCenters = useSelector((s: RootState) => s.auth.accessibleCostCenters);

  // ── Reference data ────────────────────────────────────────────────────────
  const [currencies, setCurrencies] = useState<{ code: string; name: string }[]>([]);

  useEffect(() => {
    currencyApi.getList({ pageSize: 100, isActive: true })
      .then(r => setCurrencies((r.data.data?.items ?? []).map((c: { code: string; name: string }) => ({ code: c.code, name: c.name }))))
      .catch(() => {});
  }, []);

  // ── Header state ──────────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const baseCurrency = authUser?.baseCurrencyCode ?? session?.companyCurrency ?? 'USD';

  const [supplierId,        setSupplierId]        = useState('');
  const [supplierCode,      setSupplierCode]      = useState('');
  const [supplierName,      setSupplierName]      = useState('');
  const [controlAccountId,  setControlAccountId]  = useState<string | null>(null);
  const [paymentDate,       setPaymentDate]       = useState(today);
  const [postingDate,       setPostingDate]       = useState(today);
  const [currencyCode,      setCurrencyCode]      = useState(baseCurrency);
  const [exchangeRate,      setExchangeRate]      = useState(1);
  const [reference,         setReference]         = useState('');
  const [narration,         setNarration]         = useState('');

  // ── Cheque payee (GAP-004) ──────────────────────────────────────────────────
  // Two pieces of state, not one: what will print, and what it would print with no override.
  // Without the second there is no way to offer "back to the default", and no way to tell the
  // operator that the name they are looking at is a deliberate default rather than the supplier's
  // own name — which is the difference between leaving it alone and checking it.
  const [payeeName,        setPayeeName]        = useState('');

  // Withholding: the rule picked here, the rules a supplier payment may use, and the rule a saved
  // voucher already carries (shown even if it has since been deactivated, so it does not silently
  // read as "None").
  const [withholdingRuleId,   setWithholdingRuleId]   = useState('');
  const [savedWithholdingCode, setSavedWithholdingCode] = useState<string | null>(null);
  const [withholdingRules,    setWithholdingRules]    = useState<WithholdingTaxRule[]>([]);

  useEffect(() => {
    taxApi.getWithholdingRules({ isActive: true, pageSize: 200 })
      .then(r => setWithholdingRules(
        (r.data?.data?.items ?? []).filter(x => x.appliesTo === 'Supplier' || x.appliesTo === 'Both')))
      .catch(() => setWithholdingRules([]));
  }, []);
  const [defaultPayee,     setDefaultPayee]     = useState<{ name: string; source: string } | null>(null);
  const [locationId,        setLocationId]        = useState('');
  const [costCenterId,      setCostCenterId]      = useState('');

  const [supplierOpen, setSupplierOpen] = useState(false);

  const fetchSuppliers = async (search: string, page: number, pageSize: number) => {
    const res = await supplierApi.getList({ search, pageNumber: page, pageSize, isActive: true });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  // Seed location/CC from session on new
  useEffect(() => {
    if (!isNew) return;
    setLocationId(session?.locationId ?? '');
    setCostCenterId(session?.costCenterId ?? '');
  }, [isNew, session?.locationId, session?.costCenterId]);

  // Auto-fetch exchange rate
  useEffect(() => {
    if (!currencyCode || currencyCode === baseCurrency) {
      if (isNew) setExchangeRate(1);
      return;
    }
    exchangeRateApi.getList({ fromCurrencyCode: baseCurrency, toCurrencyCode: currencyCode, pageSize: 10 })
      .then(res => {
        const items = res.data?.data?.items ?? [];
        const sorted = items.filter((r: { effectiveDate: string }) => r.effectiveDate <= paymentDate)
          .sort((a: { effectiveDate: string }, b: { effectiveDate: string }) => b.effectiveDate.localeCompare(a.effectiveDate));
        const rate = sorted.length > 0 ? sorted[0].rate : items.length > 0 ? items[0].rate : null;
        if (rate == null) return;
        setExchangeRate(rate);
        // Lines the user just moved to this currency take its rate too (see changeCurrency).
        if (syncLineRates.current) {
          syncLineRates.current = false;
          setLines(ls => ls.map(l => l.currencyCode === currencyCode ? { ...l, exchangeRate: rate } : l));
        }
      }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyCode, paymentDate]);

  // ── Which methods this company actually permits ───────────────────────────
  //
  // The dropdown used to list every method the enum knows. cfg_payment_method says which are
  // allowed in which direction, and the save validator enforces it — so a company that bars cards
  // from AP still offered Credit Card here, and choosing it left the operator with an empty,
  // disabled "Pay from", a refusal on save, and nothing saying why.
  //
  // Falls back to the full list if the call fails: a lookup that did not answer is not a policy,
  // and an empty dropdown would be a worse failure than an over-generous one the server refuses.
  const [allowedMethods, setAllowedMethods] = useState<PaymentMethodType[]>(PAYMENT_METHODS);

  useEffect(() => {
    accountApi.allowedPaymentMethods(true)
      .then(res => {
        const permitted = res.data.data ?? [];
        const offered = PAYMENT_METHODS.filter(m => permitted.includes(m));
        if (offered.length > 0) setAllowedMethods(offered);
      })
      .catch(() => {});
  }, []);

  // ── Lines ─────────────────────────────────────────────────────────────────
  const [lines, setLines] = useState<PaymentLineRow[]>([mkLine(1, baseCurrency, 1)]);

  // Changing the voucher's currency carries the lines that were on the old one with it (QC P7-102).
  // They used to stay on the old currency — a new payment opened in USD and switched to LKR kept a
  // USD line, so "Pay from" filtered to USD accounts and the LKR accounts holding the cheque books
  // were never offered. Only on a user's change, never on load: a saved voucher may mix currencies
  // on purpose. A line set to some other currency by hand is left alone.
  const syncLineRates = useRef(false);
  const changeCurrency = (next: string) => {
    setLines(ls => ls.map(l => l.currencyCode === currencyCode
      ? { ...l, currencyCode: next, exchangeRate: next === baseCurrency ? 1 : l.exchangeRate }
      : l));
    syncLineRates.current = next !== baseCurrency;
    setCurrencyCode(next);
  };

  const updateLine = (key: string, patch: Partial<PaymentLineRow>) =>
    setLines(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l));

  const addLine = () =>
    setLines(ls => [...ls, mkLine(ls.length + 1, currencyCode, exchangeRate)]);

  const removeLine = (key: string) =>
    setLines(ls => ls.filter(l => l.key !== key));

  // The lines are the money that leaves. With a withholding rule the voucher settles more than
  // that: the supplier is owed the gross, and the difference is held back for the tax authority.
  const totalAmount = lines.reduce((s, l) => s + (l.amount || 0), 0);
  const selectedRule = withholdingRules.find(r => r.id === withholdingRuleId);
  const withheldAmount = selectedRule ? withheldOnNet(totalAmount, selectedRule.withholdingPercent) : 0;
  const grossAmount = totalAmount + withheldAmount;
  // Mirrors the server's own check (WithholdingCalculator.CalculateAsync): below the rule's
  // threshold, nothing is actually withheld and the rule is refused at save. The figures above
  // still show the arithmetic — this only warns that it won't take effect, so the preview can't
  // show a confident number for something that's about to be rejected.
  const belowWithholdingThreshold =
    !!selectedRule && grossAmount * exchangeRate < selectedRule.thresholdAmount;

  // Sent for the list's Payment Method column and its filter. The server recomputes it from the
  // lines on save, so this is derived the same way rather than asked for again (ML-01).
  const headerPaymentMethod = deriveVoucherPaymentMethod<PaymentMethodType>(lines, 'Cash');

  // ── Bills this payment settles ────────────────────────────────────────────
  // Applied server-side at posting, in the same transaction that creates the credit.
  const [allocations, setAllocations] = useState<VoucherAllocationPick[]>([]);
  // The allocations as saved, with their document numbers — what a read-only voucher shows (ADR-003).
  const [savedAllocations, setSavedAllocations] = useState<SavedVoucherAllocation[] | undefined>(undefined);
  // Reported by the picker, which is the only place that knows which picks are advances. Summing
  // every allocation against the tender was right until an advance could fund part of the
  // settlement, and then it refused exactly the vouchers ML-07 exists to allow.
  const [settlement, setSettlement] = useState<VoucherAllocationSummary>(
    { settling: 0, advanceApplied: 0, overApplied: false, advanceStranded: false });

  const overAllocated = settlement.overApplied || settlement.advanceStranded;

  // ── Payment state ─────────────────────────────────────────────────────────
  const [payment,  setPayment]  = useState<ApPaymentDto | null>(null);
  const [loading,  setLoading]  = useState(!isNew);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [success,  setSuccess]  = useState<string | null>(null);
  const [tab,      setTab]      = useState<Tab>('lines');
  const [modal,    setModal]    = useState<'approve' | 'reject' | 'reverse' | 'cancel' | 'delete' | null>(null);
  const [printOpen, setPrintOpen] = useState(false);

  /**
   * Lines whose detail is open. Everything a cheque line needs beyond its number — how it is
   * crossed, which books the account can draw from — lives there rather than in the grid, where
   * it made one row five text lines tall and stretched every other cell beside it.
   */
  const [openDetail, setOpenDetail] = useState<Set<string>>(new Set());

  const toggleDetail = (key: string) =>
    setOpenDetail(open => {
      const next = new Set(open);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  // ── Load payment ──────────────────────────────────────────────────────────
  const loadPayment = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apPaymentApi.getById(id);
      const pmt = res.data?.data;
      if (!pmt) throw new Error('Payment not found');
      setPayment(pmt);

      setSupplierId(pmt.supplierId);
      setSupplierCode(pmt.supplierCode);
      setSupplierName(pmt.supplierName);
      setControlAccountId(pmt.controlAccountId ?? null);
      setPaymentDate(pmt.paymentDate.split('T')[0]);
      setPostingDate(pmt.postingDate.split('T')[0]);
      setCurrencyCode(pmt.currencyCode);
      setExchangeRate(pmt.exchangeRate);
      setReference(pmt.reference ?? '');
      setNarration(pmt.narration ?? '');
      setPayeeName(pmt.payeeName ?? '');
      setWithholdingRuleId(pmt.withholdingTaxRuleId ?? '');
      setSavedWithholdingCode(pmt.withholdingRuleCode ?? null);
      if (pmt.defaultPayeeName) {
        setDefaultPayee({ name: pmt.defaultPayeeName, source: pmt.defaultPayeeFrom ?? '' });
      }
      setLocationId(pmt.locationId ?? '');
      setCostCenterId(pmt.costCenterId ?? '');

      setSavedAllocations(pmt.allocations ?? []);
      setAllocations((pmt.allocations ?? []).map(a => ({
        openItemId:      a.openItemId,
        allocatedAmount: a.allocatedAmount,
      })));

      setLines(pmt.lines.map(l => ({
        key:           l.id,
        lineNumber:    l.lineNumber,
        paymentType:   l.paymentType as PaymentMethodType,
        // Vouchers saved before ML-01 could leave a line's bank account blank and inherit the
        // header's. The header no longer carries one, so the value moves onto the line here —
        // otherwise re-saving an old voucher would strip the account it was paying from.
        bankAccountId: l.bankAccountId ?? pmt.bankAccountId ?? '',
        fundingGlAccountId: l.fundingGlAccountId ?? '',
        pettyCashFloatId: l.pettyCashFloatId ?? '',
        // Not round-tripped: which book a saved number came from is already recorded on the leaf,
        // and re-deriving it here would be a second, drift-prone copy of the same fact.
        chequeBookId:  '',
        chequeNumber:  l.chequeNumber ?? '',
        // Round-tripped so re-saving an endorsement does not silently drop which cheque was
        // handed over — the server would then refuse the line it had already accepted.
        endorsedChequeId: l.endorsedChequeId ?? '',
        crossingType:  l.crossingType ?? '',
        chequeDate:    l.chequeDate ? l.chequeDate.split('T')[0] : '',
        cardReference: l.cardReference ?? '',
        amount:        l.amount,
        currencyCode:  l.currencyCode,
        exchangeRate:  l.exchangeRate,
        remarks:       l.remarks ?? '',
      })));

      dispatch(setErpContext({
        companyCode:         authUser?.companyId?.substring(0, 8).toUpperCase() ?? 'COMPANY',
        companyName:         '',
        locationCode:        null,
        locationName:        null,
        costCenterCode:      null,
        costCenterName:      null,
        financialYearCode:   pmt.financialYearCode,
        financialPeriodCode: pmt.financialPeriodCode,
        transactionTypeCode: 'APY',
        documentNumber:      pmt.paymentNumber,
        currencyCode:        pmt.currencyCode,
        exchangeRate:        pmt.exchangeRate,
      }));
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load payment');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, dispatch, authUser]);

  useEffect(() => { loadPayment(); }, [loadPayment]);
  useEffect(() => { return () => { dispatch(clearErpContext()); }; }, [dispatch]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const status = payment?.workflowStatus ?? 'Draft';
  const isDraftEditable = isNew || status === 'Draft' || status === 'Rejected';
  const isEditable      = isDraftEditable && (isNew ? hasPermission(Permissions.AP_PAYMENT_CREATE) : canEdit);

  // ── Build payload ─────────────────────────────────────────────────────────
  const buildAllocations = (): ApPaymentAllocationRequest[] =>
    allocations
      .filter(a => a.allocatedAmount > 0)
      .map(a => ({ openItemId: a.openItemId, allocatedAmount: a.allocatedAmount, remarks: null }));

  const buildLines = (): ApPaymentLineRequest[] =>
    lines.filter(l => l.amount > 0).map((l, idx) => ({
      lineNumber:    idx + 1,
      paymentType:   l.paymentType,
      bankAccountId: l.bankAccountId || null,
      endorsedChequeId: l.endorsedChequeId || null,
      crossingType:  l.crossingType || null,
      fundingGlAccountId: l.fundingGlAccountId || null,
      pettyCashFloatId: l.pettyCashFloatId || null,
      chequeNumber:  l.chequeNumber || null,
      chequeDate:    l.chequeDate || null,
      cardReference: l.cardReference || null,
      amount:        l.amount,
      currencyCode:  l.currencyCode || currencyCode,
      exchangeRate:  l.exchangeRate || exchangeRate,
      remarks:       l.remarks || null,
    }));

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null);
    if (!supplierId) { setError('Please select a supplier.'); return; }
    const linesPayload = buildLines();
    if (linesPayload.length === 0) { setError('At least one payment line with amount > 0 is required.'); return; }
    if (totalAmount <= 0) { setError('Payment total must be greater than zero.'); return; }
    if (overAllocated) {
      setError(settlement.advanceStranded
        ? 'This payment applies more advance than it settles. Settle more, or apply less of it.'
        : 'This payment settles more than it pays and its advances cover. '
        + 'Reduce what it settles, increase the amount paid, or apply an advance.');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const selectedLoc = accessibleLocations.find(l => l.id === locationId);
        const payload: CreateApPaymentRequest = {
          locationId:          locationId   || null,
          costCenterId:        costCenterId || null,
          financialYearCode:   session?.financialYearCode   ?? '',
          financialPeriodCode: session?.financialPeriodCode ?? '',
          supplierId,
          paymentDate, postingDate,
          paymentMethod: headerPaymentMethod,
          // Each line names where its own money comes from (ML-01). Sending a header account would
          // reinstate the inheritance the lines are meant to replace.
          bankAccountId: null,
          currencyCode, exchangeRate,
          reference: reference || null,
          narration: narration || null,
          payeeName: payeeName || null,
          withholdingTaxRuleId: withholdingRuleId || null,
          lines: linesPayload,
          allocations: buildAllocations(),
        };
        // include locationCode if available — some backends want it
        void selectedLoc; // may be used by server-side validation
        const res = await apPaymentApi.create(payload);
        const newId = res.data?.data?.id;
        setSuccess('Payment created successfully.');
        setTimeout(() => navigate(`/ap/payments/${newId}`), 800);
      } else if (payment) {
        const payload: UpdateApPaymentRequest = {
          locationId:       locationId   || null,
          costCenterId:     costCenterId || null,
          supplierId, supplierCode, supplierName,
          controlAccountId: controlAccountId ?? null,
          paymentDate, postingDate,
          paymentMethod: headerPaymentMethod,
          bankAccountId: null,
          currencyCode, exchangeRate,
          reference: reference || null,
          narration: narration || null,
          payeeName: payeeName || null,
          withholdingTaxRuleId: withholdingRuleId || null,
          lines: linesPayload,
          allocations: buildAllocations(),
        };
        await apPaymentApi.update(payment.id, payload);
        setSuccess('Payment updated.');
        setTimeout(() => { setSuccess(null); loadPayment(); }, 800);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to save payment.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Workflow actions ──────────────────────────────────────────────────────
  const doAction = async (action: () => Promise<void>, successMsg: string) => {
    setError(null);
    setSaving(true);
    try {
      await action();
      setSuccess(successMsg);
      setTimeout(() => { setSuccess(null); loadPayment(); }, 1200);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Action failed.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit  = () => doAction(async () => { await apPaymentApi.submit(payment!.id); }, 'Payment submitted for approval.');
  const handlePost    = () => doAction(async () => { await apPaymentApi.post(payment!.id); }, 'Payment posted to general ledger.');
  const handleApprove = (remarks: string) => doAction(async () => { await apPaymentApi.approve(payment!.id, remarks); }, 'Payment approved.');
  const handleReject  = (reason: string)  => doAction(async () => { await apPaymentApi.reject(payment!.id, reason); }, 'Payment rejected.');
  const handleCancel  = (reason: string)  => doAction(async () => { await apPaymentApi.cancel(payment!.id, reason); }, 'Payment cancelled.');

  const handleDelete = async () => {
    if (!payment) return;
    setSaving(true); setError(null);
    try {
      await apPaymentApi.delete(payment.id);
      navigate(AppRoutes.AP_PAYMENTS, { replace: true });
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        ?? (e as { message?: string })?.message
        ?? 'Delete failed.';
      setError(msg);
    } finally { setSaving(false); }
  };

  const handleReverse = (narr: string) => doAction(async () => {
    const today2 = new Date().toISOString().split('T')[0];
    const res = await apPaymentApi.reverse(payment!.id, today2, today2, narr || undefined);
    const revId = res.data?.data?.id;
    setTimeout(() => navigate(`/ap/payments/${revId}`), 1200);
  }, 'Reversal payment created.');

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const lineColumns: LineColumn<PaymentLineRow>[] = [
    {
      key: 'payment-type', header: 'Payment Type', width: 'w-36',
      render: (line, idx) => (
        <>
          {isEditable ? (
            <Select value={line.paymentType} size="xs"
              aria-label={`Payment type line ${idx + 1}`}
              onChange={e => {
                const method = e.target.value as PaymentMethodType;
                // Blank what the old method owned. Left behind it is invisible on
                // screen — the inputs merely disable — and still reaches the server.
                updateLine(line.key, {
                  ...clearedByMethodChange(method),
                  paymentType: method,
                  // The cheque date is mandatory where the company configures it so
                  // (QC Part III item 3). Defaulted to the payment date rather than
                  // left blank: a cheque written today carries today, and making the
                  // operator retype what the voucher already says would be a nag
                  // rather than a control. A post-dated cheque is changed from here.
                  ...(method === 'Cheque' && !line.chequeDate
                    ? { chequeDate: paymentDate }
                    : {}),
                });

                // Choosing Cheque IS the moment the crossing and the book ranges
                // become relevant, so the detail opens with it rather than behind a
                // second click. Collapsing stays available for anyone who does not
                // want it.
                if (method === 'Cheque')
                  setOpenDetail(open => new Set(open).add(line.key));
              }}
              // A saved line may name a method the company has since stopped allowing.
              // Keep it in the list so an existing voucher still shows what it says.
              options={(allowedMethods.includes(line.paymentType)
                  ? allowedMethods
                  : [...allowedMethods, line.paymentType]
                ).map(m => ({ value: m, label: PAYMENT_METHOD_LABELS[m] }))}
            />
          ) : (
            <span className="text-sm text-slate-700 dark:text-slate-300">
              {PAYMENT_METHOD_LABELS[line.paymentType]}
            </span>
          )}
        </>
      ),
    },
    {
      key: 'pay-from', header: 'Pay From', width: 'w-64',
      render: (line) => (
        <>
          <FundingSourceSelect
            paymentMethod={line.paymentType}
            forAp
            disabled={!isEditable}
            locationId={locationId || null}
            currencyCode={line.currencyCode || null}
            value={{
              bankAccountId:      line.bankAccountId || null,
              fundingGlAccountId: line.fundingGlAccountId || null,
              pettyCashFloatId:   line.pettyCashFloatId || null,
            }}
            onChange={v => updateLine(line.key, {
              bankAccountId:      v.bankAccountId ?? '',
              fundingGlAccountId: v.fundingGlAccountId ?? '',
              pettyCashFloatId:   v.pettyCashFloatId ?? '',
            })}
          />
        </>
      ),
    },
    {
      key: 'cheque', header: 'Cheque #', width: 'w-40', className: 'align-top',
      render: (line, idx) => (
        <>
          {line.paymentType === 'Cheque' ? (
            <ChequeNumberField
              bankAccountId={line.bankAccountId || null}
              // Paying by cheque from a GL account rather than a bank means handing
              // over one we already hold (ML-12).
              endorsing={!line.bankAccountId && !!line.fundingGlAccountId}
              disabled={!isEditable}
              value={{
                chequeNumber: line.chequeNumber,
                chequeBookId: line.chequeBookId,
                endorsedChequeId: line.endorsedChequeId,
              }}
              onChange={v => updateLine(line.key, {
                chequeNumber: v.chequeNumber,
                chequeBookId: v.chequeBookId,
                endorsedChequeId: v.endorsedChequeId ?? '',
              })}
            />
          ) : (
            <Input size="xs" value="" disabled placeholder="—" aria-label={`Cheque number line ${idx + 1}`} />
          )}
        </>
      ),
    },
    {
      key: 'cheque-date', header: 'Cheque Date', width: 'w-32',
      render: (line, idx) => (
        <>
          <Input size="xs" type="date" value={line.chequeDate}
            aria-label={`Cheque date line ${idx + 1}`}
            onChange={e => updateLine(line.key, { chequeDate: e.target.value })}
            disabled={!isEditable || line.paymentType !== 'Cheque'}
          />
        </>
      ),
    },
    {
      key: 'card-ref-no', header: 'Card/Ref No.', width: 'w-32',
      render: (line, idx) => (
        <>
          <Input size="xs" value={line.cardReference}
            aria-label={`Card or reference line ${idx + 1}`}
            onChange={e => updateLine(line.key, { cardReference: e.target.value })}
            disabled={!isEditable || !['CreditCard', 'DebitCard', 'BankTransfer', 'OnlineBanking'].includes(line.paymentType)}
            placeholder={['CreditCard', 'DebitCard', 'BankTransfer', 'OnlineBanking'].includes(line.paymentType) ? 'Reference…' : '—'}
          />
        </>
      ),
    },
    {
      key: 'amount', header: 'Amount', align: 'right', width: 'w-32',
      render: (line, idx) => (
        <>
          <Input size="xs" type="number" value={line.amount || ''}
            aria-label={`Amount line ${idx + 1}`}
            onChange={e => updateLine(line.key, { amount: parseFloat(e.target.value) || 0 })}
            disabled={!isEditable} min="0" step="0.01"
            className="text-right font-mono font-semibold"
          />
        </>
      ),
    },
    {
      key: 'ccy', header: 'CCY', width: 'w-20', className: 'text-xs font-mono text-slate-500 dark:text-gray-400',
      render: (line, idx) => (
        <>
          {currencies.length > 0 && isEditable ? (
            <Select size="xs" value={line.currencyCode || currencyCode}
              aria-label={`Currency line ${idx + 1}`}
              onChange={e => updateLine(line.key, { currencyCode: e.target.value })}
              className="font-mono"
              options={currencies.map(c => ({ value: c.code, label: c.code }))}
            />
          ) : (
            <span>{line.currencyCode || currencyCode}</span>
          )}
        </>
      ),
    },
    {
      key: 'fx-rate', header: 'FX Rate', width: 'w-24',
      render: (line, idx) => (
        <>
          <Input size="xs" type="number" value={line.exchangeRate || ''}
            aria-label={`Exchange rate line ${idx + 1}`}
            onChange={e => updateLine(line.key, { exchangeRate: parseFloat(e.target.value) || 1 })}
            disabled={!isEditable} step="0.000001" min="0.000001"
            className="font-mono"
          />
        </>
      ),
    },
    {
      key: 'remarks', header: 'Remarks', width: 'min-w-[140px]',
      render: (line, idx) => (
        <>
          <Input size="xs" value={line.remarks}
            aria-label={`Remarks line ${idx + 1}`}
            onChange={e => updateLine(line.key, { remarks: e.target.value })}
            disabled={!isEditable} placeholder="Note…"
          />
        </>
      ),
    },
  ];

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">

      {/* ERP Context Bar */}
      <ERPContextBar />

      <MasterToolbar
        onRefresh={() => void loadPayment()}
        refreshEnabled={!isNew}
        onSave={() => void handleSave()}
        saveEnabled={isEditable}
        onEdit={() => {}}
        onPrint={() => setPrintOpen(true)}
        printEnabled={!isNew && !!payment}
        onDelete={() => setModal('delete')}
        deleteEnabled={!isNew && status === 'Draft' && canDelete}
        onExit={() => navigate(AppRoutes.AP_PAYMENTS)}
        saving={saving}
        actions={[
          ...(!isNew && status === 'Draft' && canSubmit
            ? [{ key: 'submit', label: 'Submit', icon: <Send size={15} />, color: 'sky' as const, onClick: () => void handleSubmit() }] : []),
          ...(!isNew && (status === 'Submitted' || status === 'PendingApproval') && canApprove
            ? [{ key: 'approve', label: 'Approve', icon: <CheckCircle2 size={15} />, color: 'emerald' as const, onClick: () => setModal('approve') }] : []),
          ...(!isNew && (status === 'Submitted' || status === 'PendingApproval') && canReject
            ? [{ key: 'reject', label: 'Reject', icon: <XCircle size={15} />, color: 'rose' as const, onClick: () => setModal('reject') }] : []),
          ...(!isNew && status === 'Approved' && canPost
            ? [{ key: 'post', label: 'Post', icon: <BookCheck size={15} />, color: 'teal' as const, onClick: () => void handlePost() }] : []),
          ...(!isNew && status === 'Posted' && canReverse && !payment?.reversalPaymentId
            ? [{ key: 'reverse', label: 'Reverse', icon: <RotateCcw size={15} />, color: 'amber' as const, onClick: () => setModal('reverse') }] : []),
          ...(!isNew && !['Posted', 'Cancelled', 'Reversed'].includes(status) && canCancel
            ? [{ key: 'cancel', label: 'Cancel', icon: <Ban size={15} />, color: 'rose' as const, onClick: () => setModal('cancel') }] : []),
        ]}
      />

      <MasterFlash message={error ? { type: 'error', text: error } : success ? { type: 'success', text: success } : null} />

      <PageHeader
        icon={Banknote}
        title={isNew ? 'New AP Payment' : `Payment ${payment?.paymentNumber ?? ''}`}
        subtitle={!isNew && payment
          ? `Created ${new Date(payment.createdDate).toLocaleString()}${payment.modifiedDate ? ` · Modified ${new Date(payment.modifiedDate).toLocaleString()}` : ''}`
          : undefined}
        actions={!isNew ? (
          <div className="flex items-center gap-2">
            {payment?.originalPaymentId && (
              <Button variant="ghost" size="xs" rightIcon={<ExternalLink size={10} />}
                onClick={() => navigate(`/ap/payments/${payment.originalPaymentId}`)}>View Original</Button>
            )}
            {payment?.reversalPaymentId && (
              <Button variant="ghost" size="xs" rightIcon={<ExternalLink size={10} />}
                onClick={() => navigate(`/ap/payments/${payment.reversalPaymentId}`)}>View Reversal</Button>
            )}
            {payment?.sourceGlJournalId && (
              <Button variant="ghost" size="xs" rightIcon={<ExternalLink size={10} />}
                onClick={() => navigate(`/gl/journals/${payment.sourceGlJournalId}`)}>View GL Journal</Button>
            )}
            {payment?.isReversal && (
              <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs font-semibold rounded-full">
                Reversal
              </span>
            )}
            <span className={cn('px-3 py-1 rounded-full text-xs font-semibold', STATUS_COLORS[status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
              {status}
            </span>
          </div>
        ) : undefined}
      />

      {/* Header fields */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">

          {/* Supplier — found through the search modal (server-side search), shown as code — name */}
          <Field label="Supplier *" className="col-span-2">
            <div className="flex items-center gap-1.5">
              <Input
                readOnly
                aria-label="Supplier"
                value={supplierId ? `${supplierCode} — ${supplierName}` : ''}
                placeholder={isEditable ? 'Select supplier…' : '—'}
                onClick={() => isEditable && setSupplierOpen(true)}
                fieldClassName="flex-1"
                className={cn(isEditable && 'cursor-pointer')}
              />
              {isEditable && (
                <IconButton label="Search suppliers" icon={<Search size={14} />} onClick={() => setSupplierOpen(true)} />
              )}
              {isEditable && supplierId && (
                <IconButton label="Clear supplier" icon={<X size={14} />}
                  onClick={() => { setSupplierId(''); setSupplierCode(''); setSupplierName(''); setControlAccountId(null); }} />
              )}
            </div>
          </Field>

          <Input label="Payment Date *" type="date" value={paymentDate}
            onChange={e => setPaymentDate(e.target.value)} disabled={!isEditable} />

          <Input label="Posting Date *" type="date" value={postingDate}
            onChange={e => setPostingDate(e.target.value)} disabled={!isEditable} />

          {currencies.length > 0 ? (
            <Select label="Currency *" value={currencyCode} onChange={e => changeCurrency(e.target.value)}
              disabled={!isEditable} className="font-mono"
              options={currencies.map(c => ({ value: c.code, label: `${c.code} — ${c.name}` }))} />
          ) : (
            <Input label="Currency *" value={currencyCode} onChange={e => changeCurrency(e.target.value.toUpperCase())}
              disabled={!isEditable} maxLength={10} className="font-mono uppercase" />
          )}

          <Input label="Exchange Rate" type="number" value={exchangeRate}
            onChange={e => setExchangeRate(parseFloat(e.target.value) || 1)}
            disabled={!isEditable} step="0.000001" min="0.000001" />

          <Input label="Reference" value={reference} onChange={e => setReference(e.target.value)}
            disabled={!isEditable} placeholder="Optional" />

          <Select label="Location" value={locationId} onChange={e => setLocationId(e.target.value)}
            disabled={!isEditable} fieldClassName="col-span-2 md:col-span-3"
            placeholder="— All Locations —"
            options={accessibleLocations.map(l => ({ value: l.id, label: `${l.code} — ${l.name}` }))} />

          <Select label="Cost Center" value={costCenterId} onChange={e => setCostCenterId(e.target.value)}
            disabled={!isEditable} fieldClassName="col-span-2 md:col-span-3"
            placeholder="— None —"
            options={costCentresForLocation(accessibleCostCenters, locationId).map(cc => ({ value: cc.id, label: `${cc.code} — ${cc.name}` }))} />

          {/* Cheque payee (GAP-004) */}
          <div className="col-span-2 md:col-span-3 lg:col-span-3">
            <Input
              label="Cheque Payee Name"
              value={payeeName}
              onChange={e => setPayeeName(e.target.value)}
              disabled={!isEditable || !canOverridePayee}
              placeholder={supplierName || 'Select a supplier first'}
            />
            {defaultPayee && (
              <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                {payeeName.trim().toLowerCase() !== defaultPayee.name.toLowerCase() ? (
                  <>
                    <span className="text-amber-600 dark:text-amber-400 font-medium">
                      Changed for this voucher.
                    </span>{' '}
                    Default is “{defaultPayee.name}”.
                    {isEditable && canOverridePayee && (
                      <Button variant="ghost" size="xs" className="ml-1" onClick={() => setPayeeName(defaultPayee.name)}>
                        Use the default
                      </Button>
                    )}
                  </>
                ) : defaultPayee.source === 'SUPPLIER_NAME' ? (
                  <>The supplier&rsquo;s own name. Set a different one on the supplier if cheques
                  must be made out differently.</>
                ) : defaultPayee.source === 'SUPPLIER' ? (
                  <>Default held against this supplier.</>
                ) : (
                  <>Default held against the payable account.</>
                )}
              </p>
            )}
            {!canOverridePayee && isEditable && (
              <p className="mt-1 text-[11px] text-slate-400">
                You may not change the payee on a voucher.
              </p>
            )}
          </div>

          <Input label="Narration" value={narration} onChange={e => setNarration(e.target.value)}
            disabled={!isEditable} placeholder="Describe this payment…"
            fieldClassName="col-span-2 md:col-span-3 lg:col-span-3" />
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        tabs={TABS.map(t => t.label)}
        active={TABS.find(t => t.key === tab)?.label ?? TABS[0].label}
        onChange={label => setTab(TABS.find(t => t.label === label)?.key ?? 'lines')}
        className="static"
      />

      {/* Tab body */}
      <div className="flex-1 overflow-auto">

        {/* ── PAYMENT LINES ─────────────────────────────────────────────────── */}
        {tab === 'settles' && (
          <div className="p-6">
            <VoucherAllocationPicker
              canRaiseOnAccount={canRaiseOnAccount}
              onSummaryChange={setSettlement}
              partyType="Supplier"
              partyId={supplierId}
              currencyCode={currencyCode}
              tenderTotal={grossAmount}
              value={allocations}
              onChange={setAllocations}
              readOnly={!isEditable}
              saved={savedAllocations}
            />
          </div>
        )}

        {tab === 'lines' && (
          <LinesGrid
            rows={lines}
            columns={lineColumns}
            rowKey={line => line.key}
            lead={(line, idx) => (
              line.paymentType === 'Cheque' ? (
                <IconButton
                  variant="bare"
                  size="xs"
                  onClick={() => toggleDetail(line.key)}
                  label={`Cheque details line ${idx + 1}`}
                  aria-expanded={openDetail.has(line.key)}
                  title={line.crossingType
                    ? 'Crossing changed for this voucher'
                    : 'Cheque details'}
                  // An override is a departure from what the company configured, and
                  // collapsing the detail must not hide that it happened.
                  className={cn(line.crossingType && 'text-amber-500 hover:text-amber-600')}
                  icon={
                    <ChevronRight
                      size={12}
                      className={cn('transition-transform',
                        openDetail.has(line.key) && 'rotate-90')}
                    />
                  }
                />
              ) : <span className="w-[17px]" />
            )}
            detail={(line, idx) => (line.paymentType === 'Cheque' && openDetail.has(line.key)) ? (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
                <ChequeCrossingField
                  supplierId={supplierId || null}
                  bankAccountId={line.bankAccountId || null}
                  value={line.crossingType}
                  onChange={v => updateLine(line.key, { crossingType: v })}
                  canOverride={canOverrideCrossing}
                  disabled={!isEditable}
                  lineNumber={idx + 1}
                />

                <span className="text-slate-400 dark:text-slate-500">
                  Numbers:{' '}
                  <ChequeBookGuidance bankAccountId={line.bankAccountId || null} />
                </span>
              </div>
            ) : null}
            onRemove={isEditable ? line => removeLine(line.key) : undefined}
            footer={
              <>
              {isEditable && (
                <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                  <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addLine}>
                    Add Payment Line
                  </Button>
                </div>
              )}

              {(withholdingRules.length > 0 || withholdingRuleId) && (
                <div className="flex items-center justify-end gap-3 px-6 py-2 border-b border-slate-100 dark:border-slate-700">
                  <span className="text-xs text-slate-500 dark:text-gray-400">Withholding tax</span>
                  <Select
                    id="ap-withholding-rule"
                    aria-label="Withholding tax"
                    value={withholdingRuleId}
                    onChange={e => setWithholdingRuleId(e.target.value)}
                    disabled={!isEditable}
                    placeholder="None"
                    fieldClassName="w-72"
                    options={[
                      ...(withholdingRuleId && !selectedRule
                        ? [{ value: withholdingRuleId, label: savedWithholdingCode ?? 'Saved rule' }] : []),
                      ...withholdingRules.map(r => ({
                        value: r.id,
                        label: `${r.ruleCode} — ${r.withholdingPercent}% from ${fmt(r.thresholdAmount)}`,
                      })),
                    ]}
                  />
                </div>
              )}

              <div className="flex items-center justify-end gap-8 px-6 py-3 bg-slate-50 dark:bg-slate-800/80">
                {withheldAmount > 0 ? (
                  <>
                    <div className="text-right">
                      <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Paid ({currencyCode})</p>
                      <p className="text-sm font-semibold font-mono text-slate-700 dark:text-slate-300">{fmt(totalAmount)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">
                        Withheld{selectedRule ? ` (${selectedRule.withholdingPercent}%)` : ''}
                      </p>
                      <p className="text-sm font-semibold font-mono text-slate-700 dark:text-slate-300">{fmt(withheldAmount)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Settles ({currencyCode})</p>
                      <p className="text-base font-bold font-mono text-brand-700 dark:text-brand-400">{fmt(grossAmount)}</p>
                    </div>
                  </>
                ) : (
                  <div className="text-right">
                    <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Total ({currencyCode})</p>
                    <p className="text-base font-bold font-mono text-brand-700 dark:text-brand-400">{fmt(totalAmount)}</p>
                  </div>
                )}
                <div className="text-right">
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Base Amount</p>
                  <p className="text-sm font-semibold font-mono text-slate-700 dark:text-slate-300">
                    {fmt(grossAmount * exchangeRate)}
                  </p>
                </div>
              </div>
              {belowWithholdingThreshold && selectedRule && (
                <div className="flex items-start gap-2 px-6 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/50 text-amber-800 dark:text-amber-300">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <p className="text-xs">
                    Below the {fmt(selectedRule.thresholdAmount)} threshold for {selectedRule.ruleCode} —
                    this rule won&rsquo;t actually apply when saved. Remove it, or raise the amount.
                  </p>
                </div>
              )}
              </>
            }
          />
        )}

        {/* ── WORKFLOW ──────────────────────────────────────────────────────── */}
        {tab === 'workflow' && (
          <div className="p-6 space-y-6">
            {!isNew && payment && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Approval Status</h3>
                <WorkflowStatusPanel entityName="ApPayment" entityId={payment.id} />
              </div>
            )}
            {isNew && (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <History size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the payment first to track workflow.</p>
              </div>
            )}
          </div>
        )}

        {/* ── ATTACHMENTS ──────────────────────────────────────────────────── */}
        {tab === 'attachments' && (
          <div className="p-6">
            {!isNew && payment ? (
              <AttachmentPanel entityName="ApPayment" entityId={payment.id} canAdd={canAddDoc} canDelete={canDelDoc} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <Paperclip size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the payment first to add attachments.</p>
              </div>
            )}
          </div>
        )}

        {/* ── AUDIT LOG ─────────────────────────────────────────────────────── */}
        {tab === 'audit' && (
          <div className="p-6">
            {!isNew && payment ? (
              <AuditViewer entityName="ApPayment" entityId={payment.id} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <ShieldCheck size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the payment first to view audit history.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Modals */}
      {modal === 'approve' && (
        <ReasonDialog title="Approve Payment" placeholder="Approval remarks (optional)…" required={false}
          onConfirm={text => { setModal(null); handleApprove(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reject' && (
        <ReasonDialog title="Reject Payment" placeholder="Rejection reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleReject(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reverse' && (
        <ReasonDialog title="Create Reversal Payment" placeholder="Reversal narration (optional)…" required={false}
          onConfirm={text => { setModal(null); handleReverse(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'cancel' && (
        <ReasonDialog title="Cancel Payment" placeholder="Cancellation reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleCancel(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'delete' && (
        <ReasonDialog title="Delete Draft Payment"
          placeholder="This draft payment will be permanently removed. Type a note or leave blank."
          required={false}
          onConfirm={() => { setModal(null); handleDelete(); }}
          onCancel={() => setModal(null)} />
      )}

      {/* Supplier search modal */}
      <MasterSearchModal<SupplierListDto>
        isOpen={supplierOpen}
        onClose={() => setSupplierOpen(false)}
        title="Select Supplier"
        columns={SUPPLIER_COLUMNS}
        fetchData={fetchSuppliers}
        onSelect={item => {
          setSupplierId(item.id);
          setSupplierCode(item.code);
          setSupplierName(item.name);
          if (item.currencyCode) setCurrencyCode(item.currencyCode);
          setSupplierOpen(false);

          // GAP-004. Ask the server what this supplier's cheques are made out to, and show it.
          // Any override the operator had already typed is dropped, because it belonged to the
          // supplier they just replaced.
          apPaymentApi.defaultPayee(item.id)
            .then(res => {
              const d = res.data.data;
              if (!d) return;
              setDefaultPayee({ name: d.payeeName, source: d.source });
              setPayeeName(d.payeeName);
            })
            .catch(() => {
              setDefaultPayee(null);
              setPayeeName(item.name);
            });
        }}
      />

      {/* Print Preview */}
      {payment && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title={`Payment ${payment.paymentNumber}`}
          fileBaseName={`AP-Payment-${payment.paymentNumber}`}
          onDownloadExcel={() => exportToExcel({
            sheetName: 'Payment',
            title:    `AP Payment — ${payment.paymentNumber}`,
            subtitle: `Supplier: ${payment.supplierName ?? ''}   Date: ${payment.paymentDate?.slice(0, 10) ?? ''}   Currency: ${payment.currencyCode ?? ''}`,
            columns: [
              { header: '#',            key: 'lineNumber',    width: 6,  align: 'center' },
              { header: 'Payment Type', key: 'paymentType',   width: 16 },
              { header: 'Cheque No.',   key: 'chequeNumber',  width: 14 },
              { header: 'Cheque Date',  key: 'chequeDate',    width: 14 },
              { header: 'Ref / Card',   key: 'cardReference', width: 16 },
              { header: 'CCY',          key: 'currencyCode',  width: 8 },
              { header: 'FX Rate',      key: 'exchangeRate',  width: 12, numFmt: '#,##0.000000', align: 'right' },
              { header: 'Amount',       key: 'amount',        width: 16, numFmt: '#,##0.00', align: 'right' },
              { header: 'Base Amount',  key: 'baseAmount',    width: 16, numFmt: '#,##0.00', align: 'right' },
              { header: 'Remarks',      key: 'remarks',       width: 24 },
            ],
            rows: (payment.lines ?? []).map(l => ({
              lineNumber:   l.lineNumber,
              paymentType:  l.paymentType   ?? '',
              chequeNumber: l.chequeNumber  ?? '',
              chequeDate:   l.chequeDate?.slice(0, 10) ?? '',
              cardReference: l.cardReference ?? '',
              currencyCode: l.currencyCode  ?? '',
              exchangeRate: l.exchangeRate  || '',
              amount:       l.amount        || '',
              baseAmount:   l.baseAmount    || '',
              remarks:      l.remarks       ?? '',
            })),
            // The lines are the money paid, so the row under them totals the lines. The voucher's own
            // total is the gross it settles, which is larger when tax was withheld.
            totalsRow: {
              cardReference: 'TOTAL',
              amount:        (payment.lines ?? []).reduce((s, l) => s + (l.amount ?? 0), 0),
              baseAmount:    (payment.lines ?? []).reduce((s, l) => s + (l.baseAmount ?? 0), 0),
            },
          }, `AP-Payment-${payment.paymentNumber}`)}
          email={{ documentType: 'AP Payment', documentNo: payment.paymentNumber }}
        >
          <ApPaymentVoucher
            payment={payment}
            companyName={authUser?.companyId ? '' : 'myLedger ONE'}
            printedBy={authUser?.username}
          />
        </MasterReportPreview>
      )}
    </div>
  );
};
